import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useRandomSelectionStore from '../stores/randomSelectionStore';
import StatusBar from '../components/StatusBar';
import SelectionModal from '../components/SelectionModal';
import NumberAdjuster from '../components/NumberAdjuster';
import { initSpeechSynthesis, playWord } from '../utils/speechService'; // 导入公共语音服务
import { useSettings } from '../hooks/useSettings';

const RandomSelection = () => {
  const navigate = useNavigate();
  const { settings, previewVoice } = useSettings();
  const {
    grade, term, difficulty, wordCount, dictationSettings, modal, modalOptions,
    setGrade, setTerm, setDifficulty, setWordCount,
    getRandomWords,
    increaseRepeatCount, decreaseRepeatCount,
    increaseRepeatInterval, decreaseRepeatInterval,
    increaseWordInterval, decreaseWordInterval,
    openModal, closeModal
  } = useRandomSelectionStore();

  // 渲染难度指示器
  const renderDifficultyIndicator = () => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((level) => (
          <div 
            key={level}
            className={`w-4 h-4 rounded-full ${level <= difficulty ? 'bg-blue-500' : 'bg-gray-200'}`}
            onClick={() => setDifficulty(level)}
          ></div>
        ))}
      </div>
    );
  };

  // 处理滑块变化
  const handleSliderChange = (e) => {
    setWordCount(parseInt(e.target.value));
  };

  // 获取当前模态框的选项和选中值
  const getModalProps = () => {
    switch (modal.type) {
      case 'grade':
        return {
          title: '选择年级',
          options: modalOptions.grade,
          selectedOption: grade,
          onSelect: setGrade
        };
      case 'term':
        return {
          title: '选择学期',
          options: modalOptions.term,
          selectedOption: term,
          onSelect: setTerm
        };
      default:
        return {
          title: '',
          options: [],
          selectedOption: '',
          onSelect: () => {}
        };
    }
  };
   // 处理开始听写
   const handleStartDictation = () => {
    // 获取随机单词
    const randomWords = getRandomWords();
    
    // 如果没有符合条件的单词，提示用户
    if (randomWords.length === 0) {
      alert('没有找到符合条件的单词，请调整筛选条件');
      return;
    }

    // 尝试初始化语音合成系统
    const initialized = initSpeechSynthesis();
    
    // 准备传递给听写页面的数据
    const dictationData = {
      words: randomWords,
      bookInfo: {
        grade: grade === '全部' ? '全部年级' : grade,
        term: term === '全部' ? '全部学期' : term,
        difficulty: `难度 ${difficulty}`
      },
      settings: {
        // 使用本地设置
        repeatCount: dictationSettings.repeatCount,
        repeatInterval: dictationSettings.repeatInterval,
        wordInterval: dictationSettings.wordInterval,
        randomOrder: true, // 随机选择始终使用随机顺序
        // 使用全局设置中的语音相关设置
        voiceType: settings.voiceType,
        voiceId: settings.voiceId,
        voiceSpeed: settings.voiceSpeed,
        speechInitialized: initialized
      }
    };
    
    // 导航到听写页面并传递数据
    navigate('/dictation', { state: dictationData });
  };
  // 添加一个测试语音按钮的处理函数
  const handleTestVoice = () => {
    previewVoice('Hello');
  };
  return (
    <div className="min-h-screen bg-[#f9f9f9] text-[#333]">

      {/* Main Content */}
      <div className="p-5">
        {/* Navigation Header */}
        <div className="flex justify-between items-center mb-6">
          <Link to="/" className="text-blue-500">
            <i className="fa-solid fa-chevron-left mr-1"></i>
            返回
          </Link>
          <h2 className="text-xl font-bold">随机选择</h2>
          <div className="w-16"></div> {/* Spacer for alignment */}
        </div>

        {/* Content Box with Gradient */}
        <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl p-5 mb-6 text-white shadow-md">
          <div className="flex items-center mb-3">
            <div className="bg-white/20 rounded-full p-2 mr-3">
              <i className="fa-solid fa-shuffle text-xl"></i>
            </div>
            <h3 className="font-bold text-lg">随机单词听写</h3>
          </div>
          <p className="text-white/90 text-sm">
            系统将从所有课本单元中随机选择单词进行听写练习，帮助你全面复习并巩固记忆。
          </p>
        </div>

        {/* Random Settings */}
        <div className="mb-6">
          <h3 className="font-bold text-lg mb-3">单词范围</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div 
              className="p-3 border-b border-gray-100 flex justify-between items-center"
              onClick={() => openModal('grade')}
            >
              <div className="font-medium">年级范围</div>
              <div className="flex items-center text-gray-600">
                <span>{grade}</span>
                <i className="fa-solid fa-chevron-right ml-2 text-xs text-gray-400"></i>
              </div>
            </div>
            <div 
              className="p-3 border-b border-gray-100 flex justify-between items-center"
              onClick={() => openModal('term')}
            >
              <div className="font-medium">学期</div>
              <div className="flex items-center text-gray-600">
                <span>{term}</span>
                <i className="fa-solid fa-chevron-right ml-2 text-xs text-gray-400"></i>
              </div>
            </div>
            <div className="p-3 flex justify-between items-center">
              <div className="font-medium">难度</div>
              <div className="flex items-center">
                {renderDifficultyIndicator()}
              </div>
            </div>
          </div>
        </div>

        {/* Word Count Slider */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold text-lg">单词数量</h3>
            <div className="font-medium text-blue-500">{wordCount}个</div>
          </div>
          
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <input 
              type="range" 
              min="5" 
              max="30" 
              value={wordCount} 
              onChange={handleSliderChange}
              className="range-slider mb-2 w-full"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
              <span>25</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Dictation Settings */}
        <div className="mb-8">
          <h3 className="font-bold text-lg mb-3">听写设置</h3>
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-3 border-b border-gray-100 flex justify-between items-center">
              <div className="font-medium">单词重复次数</div>
              <NumberAdjuster 
                value={dictationSettings.repeatCount}
                unit="次"
                onDecrease={decreaseRepeatCount}
                onIncrease={increaseRepeatCount}
              />
            </div>
            <div className="p-3 border-b border-gray-100 flex justify-between items-center">
              <div className="font-medium">重复间隔时间</div>
              <NumberAdjuster 
                value={dictationSettings.repeatInterval}
                unit="秒"
                onDecrease={decreaseRepeatInterval}
                onIncrease={increaseRepeatInterval}
              />
            </div>
            <div className="p-3 flex justify-between items-center">
              <div className="font-medium">单词间隔时间</div>
              <NumberAdjuster 
                value={dictationSettings.wordInterval}
                unit="秒"
                onDecrease={decreaseWordInterval}
                onIncrease={increaseWordInterval}
              />
            </div>
            {/* 添加测试语音按钮 */}
          <div className="p-3 flex justify-between items-center">
            <div className="font-medium">测试语音</div>
            <button 
              className="bg-blue-50 text-blue-600 px-3 py-1 rounded-lg text-sm"
              onClick={handleTestVoice}
            >
              <i className="fa-solid fa-volume-high mr-1"></i>
              播放测试
            </button>
          </div>
          </div>
        </div>

        {/* Start Button */}
        <button 
          className="w-full bg-purple-600 text-white py-4 rounded-xl font-bold text-lg shadow-md flex items-center justify-center"
          onClick={handleStartDictation}
        >
          <i className="fa-solid fa-play mr-2"></i>
          开始听写
        </button>
      </div>

      {/* 选择模态框 */}
      <SelectionModal 
        isOpen={modal.isOpen}
        onClose={closeModal}
        {...getModalProps()}
      />
    </div>
  );
};

export default RandomSelection;